import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  type OnDestroy,
  type OnInit,
} from '@angular/core';
import { FormBuilder, Validators, type AbstractControl, type FormGroup } from '@angular/forms';
import { Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Subscription, combineLatest, take, type Observable } from 'rxjs';

import { GtmService } from '@fcom/common/gtm';
import { type SelectOption } from '@fcom/common/interfaces';
import { FormatCardNumberPipe } from '@fcom/common/pipes';
import { ScrollService } from '@fcom/common/services';
import { SentryLogger } from '@fcom/core';
import { type CmsTemplate } from '@fcom/core-api';
import { GIFT_CARD_ID_PATTERN, GIFT_CARD_PIN_PATTERN, enumValues, stopPropagation } from '@fcom/core/utils';
import {
  bicValidator,
  disableConditionalFields,
  enableConditionalFields,
  getCountryOptions,
  ibanValidator,
  trackFormState,
} from '@fcom/service-forms';
import { AnalyticsFormStatus, FormStatus } from '@fcom/service-forms/interfaces';
import {
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  ButtonType,
  CheckBoxTheme,
  ModalButtons,
  NotificationLayout,
  NotificationTheme,
} from '@fcom/ui-components';
import { stripSpaces } from '@fcom/ui-components/utils/string.utils';
import { LanguageService } from '@fcom/ui-translate';

import {
  EnabledOnlineBankFields,
  GiftCardInformationFormControls,
  GiftCardInformationFormName,
  GiftCardInformationFormStep,
  GiftCardStatus,
  PaymentDetailsInputs,
  RefundFormCountries,
  type GiftCardConversionError,
  type GiftCardConversionPayload,
  type GiftCardInformationForm,
  type GiftCardStepForms,
  type GiftCardVerificationError,
  type GiftCardVerificationPayload,
  type GiftCardVerificationResponse,
} from '../../interfaces';
import { GiftCardService } from '../../services/gift-card.service';
import { SubmitService } from '../../services/submit.service';
import { PAYMENT_IFSC_BIK_PATTERN, PAYMENT_INN_PATTERN, PAYMENT_KPP_PATTERN } from '../../utils';

export const EMPTY_GIFT_CARD_DATA: GiftCardVerificationResponse = {
  availableValue: '',
  currency: '',
  customer: {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    locale: '',
  },
  expiryDate: '',
  giftCardId: '',
  initialValue: '',
  isConvertibleToCash: false,
  issuanceDate: '',
  reason: {
    caseId: '',
    category: '',
    incidentDate: '',
    incidentNumber: '',
    migrated: false,
    recLoc: '',
    subCategory: '',
    user: '',
  },
  status: '' as GiftCardStatus,
};

const GIFT_CARD_CONVERSION_EVENT_NAME = 'gift-card-conversion';
const GIFT_CARD_VERIFICATION_EVENT_NAME = 'gift-card-verification';

export const GIFT_CARD_FORM_NAME = 'gift-card-information-form';
export const GIFT_CARD_MULTIPLIER = 1.5;

export const MAX_LENGTH_ACCOUNT_HOLDER = 100;
export const MAX_LENGTH_ACCOUNT_HOLDER_ADDRESS = 255;
export const MAX_LENGTH_ACCOUNT_NUMBER = 50;
export const MAX_LENGTH_BANK_ADDRESS = 255;
export const MAX_LENGTH_BANK_COUNTRY = 255;
export const MAX_LENGTH_BANK_NAME = 100;
export const MAX_LENGTH_CBD = 200;
export const MAX_LENGTH_EMAIL_ADDRESS = 100;
export const MAX_LENGTH_ISSUER = 100;
export const MAX_LENGTH_NCC = 100;
export const MAX_LENGTH_PATRONYMIC_NAME = 100;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FormatCardNumberPipe],
  selector: 'fin-gift-card-information-form',
  styleUrls: ['./gift-card-information-form.component.scss'],
  templateUrl: './gift-card-information-form.component.html',
})
export class GiftCardInformationFormComponent implements OnInit, OnDestroy {
  @Input() template?: CmsTemplate;
  @ViewChild('top') top: ElementRef;

  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonType = ButtonType;
  readonly CheckBoxTheme = CheckBoxTheme;
  readonly FormControlName = GiftCardInformationFormControls;
  readonly FormStatus = FormStatus;
  readonly FormStep = GiftCardInformationFormStep;
  readonly GIFT_CARD_FORM_NAME = GIFT_CARD_FORM_NAME;
  readonly GiftCardStatus = GiftCardStatus;
  readonly ModalButtons = ModalButtons;
  readonly NotificationLayout = NotificationLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly PaymentDetailsInputs = PaymentDetailsInputs;
  readonly RefundFormCountries = RefundFormCountries;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  private subscriptions: Subscription = new Subscription();
  private returnUrl: string;

  countries: SelectOption[];
  countries$: Observable<SelectOption[]>;
  errorStatus: HttpErrorResponse['status'] | null = null;
  formStatus: FormStatus;
  formStep: GiftCardInformationFormStep;
  giftCardData: GiftCardVerificationResponse;
  giftCardTermsUrl$: Observable<string>;
  pageTitle: string;

  bankDetailsForm: GiftCardInformationForm[GiftCardInformationFormName.BANK_DETAILS_FORM];
  contactDetailsForm: GiftCardInformationForm[GiftCardInformationFormName.CONTACT_DETAILS_FORM];
  conversionForm: GiftCardInformationForm[GiftCardInformationFormName.CONVERSION_FORM];
  reactiveForm: FormGroup<GiftCardInformationForm>;
  verificationForm: GiftCardInformationForm[GiftCardInformationFormName.VERIFICATION_FORM];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    private finFormatCardNumber: FormatCardNumberPipe,
    private formBuilder: FormBuilder,
    private giftCardService: GiftCardService,
    private gtmService: GtmService,
    private languageService: LanguageService,
    private router: Router,
    private scrollService: ScrollService,
    private sentryLogger: SentryLogger,
    private submitService: SubmitService
  ) {}

  ngOnInit(): void {
    this.formStatus = FormStatus.INITIAL;
    this.formStep = GiftCardInformationFormStep.GIFT_CARD_VERIFICATION;
    this.giftCardData = EMPTY_GIFT_CARD_DATA;
    this.pageTitle = this.template?.header?.[0]?.title ?? '';
    this.reactiveForm = this.createGiftCardInformationForm();
    this.returnUrl = this.giftCardService.getReturnUrl();

    this.bankDetailsForm = this.reactiveForm.get(
      GiftCardInformationFormName.BANK_DETAILS_FORM
    ) as GiftCardInformationForm[GiftCardInformationFormName.BANK_DETAILS_FORM];
    this.contactDetailsForm = this.reactiveForm.get(
      GiftCardInformationFormName.CONTACT_DETAILS_FORM
    ) as GiftCardInformationForm[GiftCardInformationFormName.CONTACT_DETAILS_FORM];
    this.conversionForm = this.reactiveForm.get(
      GiftCardInformationFormName.CONVERSION_FORM
    ) as GiftCardInformationForm[GiftCardInformationFormName.CONVERSION_FORM];
    this.verificationForm = this.reactiveForm.get(
      GiftCardInformationFormName.VERIFICATION_FORM
    ) as GiftCardInformationForm[GiftCardInformationFormName.VERIFICATION_FORM];

    this.subscriptions.add(
      this.bankDetailsForm
        .get(PaymentDetailsInputs.BANK_COUNTRY)
        .valueChanges.subscribe((): void => this.enableBankFieldsForCountry())
    );

    this.subscriptions.add(
      this.verificationForm
        .get(this.FormControlName.GIFT_CARD_ID)
        .valueChanges.subscribe((value: string): void => this.formatGiftCardId(value))
    );

    this.subscriptions.add(
      combineLatest([
        getCountryOptions(this.languageService),
        this.languageService.translate('SERVICE_FORMS.serviceForms.refundForm.otherCountry'),
      ]).subscribe(([allCountries, countryName]): void => {
        const countryValues = enumValues(RefundFormCountries);
        this.countries = [
          ...allCountries
            /** @todo Enable bank details for Russia. */
            .filter((country): boolean => country.value !== RefundFormCountries.RUSSIA)
            .filter((country): boolean => countryValues.includes(country.value as RefundFormCountries))
            .sort((a, b): number => a.name.localeCompare(b.name)),
          { value: RefundFormCountries.OTHER_COUNTRIES, name: countryName },
        ];
      })
    );

    this.updateBankDetails();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Salesforce has only one field for account numbers, so the IBAN field
   * is used for both `ACCOUNT_NUMBER` and `IBAN` depending on which
   * country has been selected.
   */
  get bankAccount(): string {
    return (
      (this.bankDetailsForm?.get(PaymentDetailsInputs.ACCOUNT_NUMBER)?.enabled
        ? this.bankDetailsForm?.get(PaymentDetailsInputs.ACCOUNT_NUMBER)?.value?.trim()
        : this.bankDetailsForm?.get(PaymentDetailsInputs.IBAN)?.value?.trim()) ?? ''
    );
  }

  get bankDetailsDescription(): string {
    return 'CN' === this.languageService.ddsLocaleValue.countrySite
      ? 'SERVICE_FORMS.serviceForms.giftCardInformationForm.steps.bankDetails.descriptionChina'
      : 'SERVICE_FORMS.serviceForms.giftCardInformationForm.steps.bankDetails.description';
  }

  get bankCountry(): SelectOption | undefined {
    const countryValue: string = this.bankDetailsForm.get([PaymentDetailsInputs.BANK_COUNTRY]).value;
    return this.countries.find((country): boolean => country.value === countryValue);
  }

  get currentStepForm(): GiftCardStepForms {
    switch (this.formStep) {
      case GiftCardInformationFormStep.GIFT_CARD_VERIFICATION:
        return [this.verificationForm];
      case GiftCardInformationFormStep.GIFT_CARD_INFORMATION:
        return [];
      case GiftCardInformationFormStep.BANK_DETAILS:
        return [this.bankDetailsForm];
      case GiftCardInformationFormStep.CONTACT_DETAILS:
        return [this.contactDetailsForm];
      case GiftCardInformationFormStep.GIFT_CARD_CONVERSION:
        return [this.conversionForm];
      default:
        this.sentryLogger.error('Unknown step:', this.formStep);
        return [this.reactiveForm];
    }
  }

  get currentStepIsValid(): boolean {
    switch (this.formStep) {
      case GiftCardInformationFormStep.GIFT_CARD_VERIFICATION:
        return this.verificationForm.valid;
      case GiftCardInformationFormStep.GIFT_CARD_INFORMATION:
        return this.giftCardIsConvertible;
      case GiftCardInformationFormStep.BANK_DETAILS:
        return this.bankDetailsForm.valid;
      case GiftCardInformationFormStep.CONTACT_DETAILS:
        return this.contactDetailsForm.valid;
      case GiftCardInformationFormStep.GIFT_CARD_CONVERSION:
        return this.conversionForm.valid;
      default:
        return false;
    }
  }

  get formIsDisabled(): boolean {
    return FormStatus.LOADING === this.formStatus || FormStatus.PRECONDITION_REQUIRED === this.formStatus;
  }

  get giftCardConversionPayload(): GiftCardConversionPayload {
    return {
      body: {
        [PaymentDetailsInputs.ACCOUNT_HOLDER]: this.bankDetailsForm.get(PaymentDetailsInputs.ACCOUNT_HOLDER).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.ACCOUNT_HOLDER).value.trim()
          : undefined,
        [PaymentDetailsInputs.ACCOUNT_HOLDER_ADDRESS]: this.bankDetailsForm.get(
          PaymentDetailsInputs.ACCOUNT_HOLDER_ADDRESS
        ).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.ACCOUNT_HOLDER_ADDRESS).value.trim()
          : undefined,
        [PaymentDetailsInputs.BANK_ADDRESS]: this.bankDetailsForm.get(PaymentDetailsInputs.BANK_ADDRESS).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.BANK_ADDRESS).value.trim()
          : undefined,
        [PaymentDetailsInputs.BANK_COUNTRY]: this.bankDetailsForm.get(PaymentDetailsInputs.BANK_COUNTRY).value.trim(),
        [PaymentDetailsInputs.BANK_NAME]: this.bankDetailsForm.get(PaymentDetailsInputs.BANK_NAME).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.BANK_NAME).value.trim()
          : undefined,
        [PaymentDetailsInputs.BIC]: this.bankDetailsForm.get(PaymentDetailsInputs.BIC).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.BIC).value.trim()
          : undefined,
        [PaymentDetailsInputs.BIK]: this.bankDetailsForm.get(PaymentDetailsInputs.BIK).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.BIK).value.trim()
          : undefined,
        [PaymentDetailsInputs.CBD]: this.bankDetailsForm.get(PaymentDetailsInputs.CBD).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.CBD).value.trim()
          : undefined,
        [PaymentDetailsInputs.IBAN]: this.bankAccount || undefined,
        [PaymentDetailsInputs.IFSC]: this.bankDetailsForm.get(PaymentDetailsInputs.IFSC).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.IFSC).value.trim()
          : undefined,
        [PaymentDetailsInputs.INN]: this.bankDetailsForm.get(PaymentDetailsInputs.INN).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.INN).value.trim()
          : undefined,
        [PaymentDetailsInputs.KPP]: this.bankDetailsForm.get(PaymentDetailsInputs.KPP).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.KPP).value.trim()
          : undefined,
        [PaymentDetailsInputs.NCC]: this.bankDetailsForm.get(PaymentDetailsInputs.NCC).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.NCC).value.trim()
          : undefined,
        [PaymentDetailsInputs.PATRONYMIC_NAME]: this.bankDetailsForm.get(PaymentDetailsInputs.PATRONYMIC_NAME).enabled
          ? this.bankDetailsForm.get(PaymentDetailsInputs.PATRONYMIC_NAME).value.trim()
          : undefined,
        [GiftCardInformationFormControls.EMAIL_ADDRESS]: this.contactDetailsForm
          .get(GiftCardInformationFormControls.EMAIL_ADDRESS)
          .value.trim(),
      },
      [GiftCardInformationFormControls.GIFT_CARD_ID]: this.giftCardId,
      pin: this.giftCardPinForConversion,
    };
  }

  get giftCardId(): string {
    return stripSpaces(this.verificationForm.get(GiftCardInformationFormControls.GIFT_CARD_ID).value.trim());
  }

  get giftCardIsConvertible(): boolean {
    return GiftCardStatus.ACTIVE === this.giftCardData.status && this.giftCardData.isConvertibleToCash;
  }

  get giftCardPinForConversion(): string {
    return this.conversionForm.get(GiftCardInformationFormControls.GIFT_CARD_PIN_2).value.trim();
  }

  get giftCardPinForVerification(): string {
    return this.verificationForm.get(GiftCardInformationFormControls.GIFT_CARD_PIN_1).value.trim();
  }

  get giftCardVerificationPayload(): GiftCardVerificationPayload {
    return {
      [GiftCardInformationFormControls.GIFT_CARD_ID]: this.giftCardId,
      pin: this.giftCardPinForVerification,
    };
  }

  get invalidField(): HTMLInputElement | undefined {
    const invalidInputs = this.elementRef.nativeElement.querySelectorAll('input.ng-invalid');
    return invalidInputs?.length ? (invalidInputs.item(0) as HTMLInputElement) : undefined;
  }

  private changeStep(nextStep: GiftCardInformationFormStep, skipValidation = false): void {
    const currentStepIsValid = this.validateCurrentStep(skipValidation);

    if (!skipValidation && !currentStepIsValid) {
      this.setFormStatus(FormStatus.ERROR);
      this.submitService.scrollToInvalidField(this.currentStepForm, this.elementRef);
      this.invalidField?.focus();

      return;
    }

    this.setFormStatus(FormStatus.LOADING);

    switch (true) {
      case GiftCardInformationFormStep.GIFT_CARD_VERIFICATION === this.formStep &&
        currentStepIsValid &&
        !skipValidation:
        this.getGiftCardInformation();
        break;
      case GiftCardInformationFormStep.GIFT_CARD_CONVERSION === this.formStep && currentStepIsValid && !skipValidation:
        this.submitGiftCardConversion();
        break;
      case currentStepIsValid || skipValidation:
        this.setFormStatus(FormStatus.INITIAL);
        this.setFormStep(nextStep);
        this.scrollService.scrollToImmediate(this.top);
        break;
    }
  }

  private createGiftCardInformationForm(): FormGroup<GiftCardInformationForm> {
    return this.formBuilder.group({
      [GiftCardInformationFormName.VERIFICATION_FORM]: this.formBuilder.group({
        [GiftCardInformationFormControls.GIFT_CARD_ID]: [
          '',
          [Validators.required, Validators.pattern(GIFT_CARD_ID_PATTERN)],
        ],
        [GiftCardInformationFormControls.GIFT_CARD_PIN_1]: [
          '',
          [Validators.required, Validators.pattern(GIFT_CARD_PIN_PATTERN)],
        ],
      }),
      [GiftCardInformationFormName.BANK_DETAILS_FORM]: this.formBuilder.group({
        [PaymentDetailsInputs.ACCOUNT_HOLDER]: [
          '',
          [Validators.required, Validators.maxLength(MAX_LENGTH_ACCOUNT_HOLDER)],
        ],
        [PaymentDetailsInputs.ACCOUNT_HOLDER_ADDRESS]: [
          '',
          [Validators.required, Validators.maxLength(MAX_LENGTH_ACCOUNT_HOLDER_ADDRESS)],
        ],
        [PaymentDetailsInputs.ACCOUNT_NUMBER]: [
          '',
          [Validators.required, Validators.maxLength(MAX_LENGTH_ACCOUNT_NUMBER)],
        ],
        [PaymentDetailsInputs.BANK_ADDRESS]: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_BANK_ADDRESS)]],
        [PaymentDetailsInputs.BANK_COUNTRY]: [
          this.languageService.ddsLocaleValue.countrySite,
          [Validators.required, Validators.maxLength(MAX_LENGTH_BANK_COUNTRY)],
        ],
        [PaymentDetailsInputs.BANK_NAME]: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_BANK_NAME)]],
        [PaymentDetailsInputs.BIC]: ['', [Validators.required, bicValidator()]],
        [PaymentDetailsInputs.BIK]: ['', [Validators.required, Validators.pattern(PAYMENT_IFSC_BIK_PATTERN)]],
        [PaymentDetailsInputs.CBD]: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_CBD)]],
        [PaymentDetailsInputs.IBAN]: ['', [Validators.required, ibanValidator()]],
        [PaymentDetailsInputs.IFSC]: ['', [Validators.required, Validators.pattern(PAYMENT_IFSC_BIK_PATTERN)]],
        [PaymentDetailsInputs.INN]: ['', [Validators.required, Validators.pattern(PAYMENT_INN_PATTERN)]],
        [PaymentDetailsInputs.KPP]: ['', [Validators.required, Validators.pattern(PAYMENT_KPP_PATTERN)]],
        [PaymentDetailsInputs.NCC]: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_NCC)]],
        [PaymentDetailsInputs.PATRONYMIC_NAME]: [
          '',
          [Validators.required, Validators.maxLength(MAX_LENGTH_PATRONYMIC_NAME)],
        ],
      }) as GiftCardInformationForm[GiftCardInformationFormName.BANK_DETAILS_FORM],
      [GiftCardInformationFormName.CONTACT_DETAILS_FORM]: this.formBuilder.group({
        [GiftCardInformationFormControls.EMAIL_ADDRESS]: [
          '',
          [Validators.required, Validators.email, Validators.maxLength(MAX_LENGTH_EMAIL_ADDRESS)],
        ],
      }),
      [GiftCardInformationFormName.CONVERSION_FORM]: this.formBuilder.group({
        [GiftCardInformationFormControls.GIFT_CARD_PIN_2]: [
          '',
          [Validators.required, Validators.pattern(GIFT_CARD_PIN_PATTERN)],
        ],
      }),
    });
  }

  private enableBankFieldsForCountry(): void {
    const enabledFields = [];
    const disabledFields = [];
    const country: string = this.bankDetailsForm.get(PaymentDetailsInputs.BANK_COUNTRY).value;

    EnabledOnlineBankFields.forEach((bankField): void => {
      if (bankField.enabledCountries.includes(country) || bankField.enabledCountries.includes('ALL')) {
        enabledFields.push(bankField.name);
      } else {
        disabledFields.push(bankField.name);
      }
    });

    enableConditionalFields(enabledFields, this.bankDetailsForm);
    disableConditionalFields(disabledFields, this.bankDetailsForm);
    this.changeDetectorRef.markForCheck();
  }

  private formatGiftCardId(value: string): void {
    const formattedValue = this.finFormatCardNumber.transform(value);
    this.verificationForm.get(this.FormControlName.GIFT_CARD_ID).setValue(formattedValue, { emitEvent: false });
  }

  private handleGiftCardConversionError(response: GiftCardConversionError): void {
    if (response instanceof HttpErrorResponse && response.status === HttpStatusCode.PreconditionRequired) {
      this.setFormStatus(FormStatus.PRECONDITION_REQUIRED);
      trackFormState(GIFT_CARD_CONVERSION_EVENT_NAME, AnalyticsFormStatus.PRECONDITION_REQUIRED, this.gtmService);
      this.sentryLogger.warn('Akamai crypto challenge triggered during gift card conversion', {
        response,
      });
    } else {
      trackFormState(GIFT_CARD_CONVERSION_EVENT_NAME, AnalyticsFormStatus.ERROR, this.gtmService);
      this.setErrorStatus(response.status);
      this.setFormStatus(FormStatus.ERROR);
      this.sentryLogger.error('Failed to send request to convert gift card to cash', {
        response,
      });
    }
  }

  private handleGiftCardConversionNext(): void {
    this.setFormStatus(FormStatus.COMPLETE);
    trackFormState(GIFT_CARD_CONVERSION_EVENT_NAME, AnalyticsFormStatus.SUCCESS, this.gtmService);
    this.scrollService.scrollToImmediate(this.top);
  }

  private handleGiftCardVerificationError(response: GiftCardVerificationError): void {
    if (response instanceof HttpErrorResponse && response.status === HttpStatusCode.PreconditionRequired) {
      this.setFormStatus(FormStatus.PRECONDITION_REQUIRED);
      trackFormState(GIFT_CARD_VERIFICATION_EVENT_NAME, AnalyticsFormStatus.PRECONDITION_REQUIRED, this.gtmService);
      this.sentryLogger.warn('Akamai crypto challenge triggered during gift card verification', {
        response,
      });
    } else {
      this.setErrorStatus(response.status);
      this.setFormStatus(FormStatus.ERROR);
      trackFormState(GIFT_CARD_VERIFICATION_EVENT_NAME, AnalyticsFormStatus.ERROR, this.gtmService);
      this.sentryLogger.error('Failed to retrieve gift card information', {
        response,
      });
    }
  }

  private handleGiftCardVerificationNext(response: GiftCardVerificationResponse): void {
    trackFormState(GIFT_CARD_VERIFICATION_EVENT_NAME, AnalyticsFormStatus.SUCCESS, this.gtmService);
    this.setGiftCardData(response);
    this.setFormStatus(FormStatus.INITIAL);
    this.changeStep(this.formStep + 1, true);
    this.scrollService.scrollToImmediate(this.top);
  }

  /**
   * Gets gift card information based on the given ID and PIN.
   *
   * @see {@link https://techdocs.akamai.com/bot-manager/docs/challenge-actions#setup-for-custom-fetch-or-xmlhttprequest Setup for custom Fetch or XMLHttpRequest}
   * @returns The gift card information if accepted.
   * @throws When:
   * - Akamai returns a `428` status due to suspected bot activity
   * - the gift card information cannot be retrieved
   * - the gift card ID and PIN do not match
   */
  private getGiftCardInformation(): void {
    if (GiftCardInformationFormStep.GIFT_CARD_VERIFICATION !== this.formStep) {
      return;
    }
    this.setErrorStatus(null);
    this.setFormStatus(FormStatus.LOADING);
    trackFormState(GIFT_CARD_VERIFICATION_EVENT_NAME, AnalyticsFormStatus.SEND_ATTEMPT, this.gtmService);
    this.subscriptions.add(
      this.giftCardService
        .getGiftCardInformation(this.giftCardVerificationPayload)
        .pipe(take(1))
        .subscribe({
          error: (error: GiftCardVerificationError): void => this.handleGiftCardVerificationError(error),
          next: (response: GiftCardVerificationResponse): void => this.handleGiftCardVerificationNext(response),
        })
    );
  }

  private resetGiftCardInformation(): void {
    this.giftCardData = EMPTY_GIFT_CARD_DATA;
    this.verificationForm.reset();
    this.setGiftCardStatus(GiftCardStatus.INITIAL);
    this.changeDetectorRef.markForCheck();
  }

  private setErrorStatus(status: HttpErrorResponse['status'] | null): void {
    this.errorStatus = status;
    this.changeDetectorRef.markForCheck();
  }

  private setFormStatus(formStatus: FormStatus): void {
    this.formStatus = formStatus;
    this.changeDetectorRef.markForCheck();
  }

  private setFormStep(formStep: GiftCardInformationFormStep): void {
    this.formStep = formStep;
    this.changeDetectorRef.markForCheck();
  }

  private setGiftCardData(response: GiftCardVerificationResponse): void {
    this.giftCardData = response;
    this.setGiftCardStatus(response.status);
    this.changeDetectorRef.markForCheck();
  }

  private setGiftCardStatus(status: string): void {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
        this.giftCardData.status = GiftCardStatus.ACTIVE;
        break;
      case 'CANCELLED':
        this.giftCardData.status = GiftCardStatus.CANCELLED;
        break;
      case 'CONVERTED':
        this.giftCardData.status = GiftCardStatus.CONVERTED;
        break;
      case 'EXPIRED':
        this.giftCardData.status = GiftCardStatus.EXPIRED;
        break;
      case 'LOCKED':
        this.giftCardData.status = GiftCardStatus.LOCKED;
        break;
      default:
        this.sentryLogger.error('Unknown gift card status:', status);
        this.giftCardData.status = GiftCardStatus.INITIAL;
    }
  }

  /**
   * Submits the gift card information to trigger a conversion.
   *
   * @see {@link https://techdocs.akamai.com/bot-manager/docs/challenge-actions#setup-for-custom-fetch-or-xmlhttprequest Setup for custom Fetch or XMLHttpRequest}
   * @returns TBD.
   * @throws When:
   * - Akamai returns a `428` status due to suspected bot activity
   * - the gift card information was incomplete
   * - the gift card ID and PIN do not match
   */
  private submitGiftCardConversion(): void {
    if (GiftCardInformationFormStep.GIFT_CARD_CONVERSION !== this.formStep) {
      return;
    }
    this.setErrorStatus(null);
    this.setFormStatus(FormStatus.LOADING);
    trackFormState(GIFT_CARD_CONVERSION_EVENT_NAME, AnalyticsFormStatus.SEND_ATTEMPT, this.gtmService);
    this.subscriptions.add(
      this.giftCardService
        .postGiftCardConversion(this.giftCardConversionPayload)
        .pipe(take(1))
        .subscribe({
          error: (error: GiftCardConversionError): void => this.handleGiftCardConversionError(error),
          next: (): void => this.handleGiftCardConversionNext(),
        })
    );
  }

  private updateBankDetails(reset = false): void {
    const bankCountryControl = this.bankDetailsForm.get(PaymentDetailsInputs.BANK_COUNTRY);
    bankCountryControl.enable();
    bankCountryControl.setValidators([Validators.required]);
    if (reset) {
      bankCountryControl.reset(this.languageService.ddsLocaleValue.countrySite);
    }
    this.enableBankFieldsForCountry();
    this.changeDetectorRef.markForCheck();
  }

  private validateCurrentStep(skipValidation = false): boolean {
    if (skipValidation) {
      return true;
    } else if (GiftCardInformationFormStep.GIFT_CARD_INFORMATION === this.formStep) {
      return GiftCardStatus.ACTIVE === this.giftCardData.status && this.giftCardData.isConvertibleToCash;
    }
    return this.currentStepForm.every((form): boolean => form.valid);
  }

  @HostListener('document:akamai-challenge-noreload', ['$event'])
  handleAkamaiChallenge(event: Event): void {
    this.setFormStatus(FormStatus.INITIAL);
    if (GiftCardInformationFormStep.GIFT_CARD_VERIFICATION === this.formStep) {
      this.getGiftCardInformation();
    } else if (GiftCardInformationFormStep.GIFT_CARD_CONVERSION === this.formStep) {
      this.submitGiftCardConversion();
    } else {
      this.sentryLogger.error(`Akamai challenge triggered in an unexpected step: ${this.formStep}`, { event });
    }
  }

  @HostListener('submit', ['$event'])
  nextStep(event?: Event): void {
    stopPropagation(event);
    const nextStep = this.formStep + 1;
    this.changeStep(nextStep);
  }

  previousStep(event?: Event): void {
    stopPropagation(event);
    switch (this.formStep) {
      case GiftCardInformationFormStep.GIFT_CARD_VERIFICATION:
        this.updateBankDetails(true);
        this.router.navigateByUrl(this.returnUrl, { replaceUrl: false });
        return;
      case GiftCardInformationFormStep.GIFT_CARD_INFORMATION:
        this.resetGiftCardInformation();
        break;
      case GiftCardInformationFormStep.GIFT_CARD_CONVERSION:
        this.conversionForm.get(GiftCardInformationFormControls.GIFT_CARD_PIN_2).reset();
        break;
      default:
        break;
    }
    const previousStep = this.formStep - 1;
    this.changeStep(previousStep, true);
  }

  stripInputSpaces(formGroup: FormGroup, fieldRef: string): void {
    const formField: AbstractControl<string> = formGroup.get(fieldRef);
    formField.setValue(stripSpaces(formField.value));
  }
}
